import { m } from "#/paraglide/messages";
import {
  cn,
  createDataTableColumnHelper,
  DataTable,
  TableCellStack,
  Link as UiLink,
} from "@beweegbaas/ui";
import { createFileRoute, Outlet, stripSearchParams, useParams } from "@tanstack/react-router";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { ShortDate } from "#/components/short-date";
import { StudentResultsSidebarFilters } from "#/modules/student/components/student-results-sidebar-filters";
import {
  BEWEEGRESULTATEN_SEARCH_DEFAULTS,
  beweegresultatenSearchSchema,
  getDefaultDateRange,
} from "#/modules/student/schema.search.beweegresultaten";
import { getStudentBeweegResults } from "#/modules/school.students/students.functions";
import {
  getLeerjaarComboTranslation,
  getLeerlijnBeweegthemaLabel,
} from "#/modules/util.i18n/enum-mapping";
import {
  OverachievedIconButton,
  ScoreStatusIndicatorLabel,
} from "#/modules/util.score-status/components/score-status";
import { getScoreBasedRowColor, getScoreStatus } from "#/modules/util.score-status/score-status";
import type { components } from "#/schema";
import type { BeweegresultatenSearch } from "#/modules/student/schema.search.beweegresultaten";

function formatResultaat(
  behaaldNiveau: number | null | undefined,
  maxNiveau: number,
  type: components["schemas"]["BeweegactiviteitType"],
) {
  if (behaaldNiveau == null || behaaldNiveau <= 0) return "-";
  // Meerkeuze counts ticked doelen, so show count/max like the SE resultaten do;
  // a ladder niveau stands on its own.
  return type === "Meerkeuze" ? `${behaaldNiveau}/${maxNiveau}` : behaaldNiveau.toString();
}

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/results",
)({
  validateSearch: beweegresultatenSearchSchema,
  // Keeps default filter values out of the URL.
  search: { middlewares: [stripSearchParams(BEWEEGRESULTATEN_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the server fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    from: search.from,
    to: search.to,
    includeWithProgress: search.includeWithProgress,
    includeWithoutProgress: search.includeWithoutProgress,
    selectedLeerlijnFilterIds: search.selectedLeerlijnFilterIds,
  }),
  loader: ({ deps, params }) =>
    getStudentBeweegResults({ data: { leerlingId: params.id, ...deps } }),
  component: Beweegresultaten,
});

type ProgressItem = Awaited<
  ReturnType<typeof getStudentBeweegResults>
>["beweegactiviteitProgressItems"][number];

const helper = createDataTableColumnHelper<ProgressItem>();

const columns = helper.columns([
  helper.accessor("name", {
    header: "Beweegactiviteit",
    meta: { rowTarget: true, wrap: true },
    cell: ({ row, getValue }) => (
      <TableCellStack
        secondary={getLeerlijnBeweegthemaLabel(row.original.leerlijn, row.original.beweegthema)}
      >
        <UiLink
          variant="secondary"
          underline
          to="/$locale/{-$schoolyear}/$school/students/$id/results/drawer/historie/$beweegactiviteitId"
          params={(prev: Record<string, string>) => ({
            ...prev,
            beweegactiviteitId: row.original.beweegactiviteitId,
          })}
          search={(prev: BeweegresultatenSearch) => ({
            ...prev,
            beweegactiviteitenReeksId: row.original.beweegactiviteitenReeksId,
            leerjaarCombo: row.original.beweegactiviteitLeerjaarCombo,
          })}
        >
          {getValue()}
        </UiLink>
      </TableCellStack>
    ),
  }),
  helper.display({
    id: "leerjaar",
    header: "Leerjaar",
    meta: { cellClassName: "text-muted-foreground" },
    cell: ({ row }) => getLeerjaarComboTranslation(row.original.beweegactiviteitLeerjaarCombo),
  }),
  helper.display({
    id: "resultaat",
    header: "Resultaat",
    cell: ({ row }) =>
      row.original.behaaldNiveau === 0 ? (
        "-"
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <ScoreStatusIndicatorLabel
            status={getScoreStatus(row.original.behaaldNiveau, row.original.maxNiveau)}
            label={formatResultaat(
              row.original.behaaldNiveau,
              row.original.maxNiveau,
              row.original.type,
            )}
          />
          {row.original.isOverachieved && <OverachievedIconButton />}
        </span>
      ),
  }),
  helper.display({
    id: "laatstBehandeld",
    header: m["student.laatste_resultaat"](),
    cell: ({ row }) =>
      row.original.behaaldAt ? <ShortDate value={row.original.behaaldAt} /> : "-",
  }),
]);

function Beweegresultaten() {
  const { beweegactiviteitProgressItems, leerlijnFilter } = Route.useLoaderData();
  // Single source for both the sidebar filter controls and the table rows.
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const childParams = useParams({ strict: false });
  const selectedBeweegactiviteitId = childParams.beweegactiviteitId;
  const defaultDateRange = getDefaultDateRange();

  return (
    <Page.SidebarLayout>
      <Page.Sidebar>
        <StudentResultsSidebarFilters
          filterTree={leerlijnFilter}
          filterType="leerlijn"
          defaultDateRange={defaultDateRange}
          filters={search}
          selectedFilterIds={search.selectedLeerlijnFilterIds}
          onFiltersChange={(partial) =>
            navigate({
              search: (prev) => ({ ...prev, ...partial }),
              replace: true,
              resetScroll: false,
            })
          }
          onFilterIdsChange={(ids) =>
            navigate({
              search: (prev) => ({ ...prev, selectedLeerlijnFilterIds: ids }),
              replace: true,
              resetScroll: false,
            })
          }
        />
      </Page.Sidebar>
      <PendingContent className="grow">
        <DataTable
          card
          aria-label="Beweegresultaten"
          columns={columns}
          data={beweegactiviteitProgressItems}
          getRowId={(item) => item.beweegactiviteitId}
          empty={<p className="w-full text-center">{m["common.empty_state.no_results_found"]()}</p>}
          getRowClassName={(item) =>
            cn(
              getScoreBasedRowColor(item.behaaldNiveau),
              selectedBeweegactiviteitId === item.beweegactiviteitId &&
                `relative z-10 shadow-[0_0_10px_2px_rgba(0,0,0,0.1),0_-1px_0px_rgba(0,0,0,0.05)_inset]`,
            )
          }
        />
      </PendingContent>
      <Outlet />
    </Page.SidebarLayout>
  );
}
